<template>
  <div :class="rootClass">
    <label class="bravado-checkbox-v3__label" :class="labelClass">
      <input
        v-bind="$attrs"
        type="checkbox"
        class="bravado-checkbox-v3__input"
        @change="$emit('change', $event.target.checked)"
        @input="$emit('input', $event.target.checked)"
      />
      <span :class="textClass">
        <slot />
      </span>
    </label>
    <div v-if="!isValid && errorMessage" class="bravado-checkbox-v3__error">{{ errorMessage }}</div>
  </div>
</template>

<script>
export default {
  name: 'BravadoCheckboxV3',
  model: {
    prop: 'checked',
    event: 'change',
  },
  props: {
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md'].includes(value),
    },
    button: {
      type: Boolean,
      default: false,
    },
    isValid: {
      type: Boolean,
      default: true,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    align: {
      type: String,
      default: 'center',
      validator: value => ['center', 'top'].includes(value),
    },
    variant: {
      type: String,
      default: 'default',
      // rounded variant is a circle + doesn't have focus outline
      validator: value => ['default', 'rounded'].includes(value),
    },
  },
  computed: {
    rootClass() {
      return {
        'bravado-checkbox-v3': true,
        'bravado-checkbox-v3--error': !this.isValid,
        'bravado-checkbox-v3--button': this.button,
        [`bravado-checkbox-v3--${this.size}`]: true,
        [`bravado-checkbox-v3--${this.variant}`]: true,
      };
    },
    labelClass() {
      return [{ 'bravado-checkbox-v3__label--checked': !!this.$attrs.checked }];
    },
    textClass() {
      return ['bravado-checkbox-v3__text', `bravado-checkbox-v3__text--${this.align}`];
    },
  },
};
</script>

<style lang="scss">
.bravado-checkbox-v3 {
  $this: &;

  &--button {
    #{$this}__label {
      box-shadow: var(--theme-elevation-01);
      padding: 6px 8px;
      border-radius: 4px;
      display: inline-block;
      background: var(--theme-color-card);
    }

    #{$this}__label--checked {
      background: var(--blue-50);
    }

    #{$this}__input:checked + #{$this}__text {
      color: var(--white);

      &::before {
        background: var(--blue-50);
        border-color: var(--blue-50);
      }

      &::after {
        color: var(--white);
      }
    }
  }

  &__label {
    @include body;

    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    color: var(--theme-color-text);
    min-width: 24px;
    min-height: 24px;

    #{$this}--sm & {
      min-width: 16px;
      min-height: 16px;
    }
  }

  &__text {
    @include inherit-font;

    padding: 0 0 0 32px;
    position: relative;
    display: inline-block;

    #{$this}--sm & {
      padding-left: 24px;
    }

    &--top {
      $space: 10px;

      &::after {
        top: calc($space + 1px);
      }

      &::before {
        top: $space;
      }
    }

    &--center {
      $space: 50%;

      &::after {
        top: calc($space + 1px);
      }

      &::before {
        top: $space;
      }
    }

    &::before {
      content: '';
      box-sizing: border-box;
      position: absolute;
      width: 24px;
      height: 24px;
      border: solid 1px var(--theme-color-border);
      border-radius: 4px;
      background: var(--theme-color-card);
      left: 0;
      transform: translateY(-50%);
      transition: all 0.1s;

      #{$this}--sm & {
        width: 16px;
        height: 16px;
      }

      #{$this}--rounded & {
        border-radius: 50%;
      }
    }

    &:hover {
      color: var(--theme-color-text-hover);

      &::before {
        border-color: var(--theme-border-hover);
        transition: all 0.2s;
      }
    }
  }

  &__input {
    width: 0;
    height: 0;
    font-size: 0;
    opacity: 0;
    position: absolute;
    z-index: -1;

    // Rounded version doesn't have focus state
    #{$this}--default &:focus-visible:not([disabled]) {
      & + #{$this}__text {
        color: var(--theme-color-text-hover);

        &::before {
          outline: solid 2px var(--blue-60);
          outline-offset: 2px;
          transition: none;
        }
      }
    }

    &:checked {
      & + #{$this}__text {
        color: var(--theme-color-text);

        &::before {
          background: var(--blue-50);
          border-color: var(--blue-50);
        }

        &::after {
          content: '';
          box-sizing: border-box;
          position: absolute;
          width: 16px;
          height: 16px;
          left: 4px;
          transform: translateY(-50%);
          z-index: 2;
          background-image: url('./assets/checkIcon-active.svg');
          background-repeat: no-repeat;
          background-position: center;

          #{$this}--sm & {
            width: 12px;
            height: 12px;
            background-size: 12px;
            left: 2px;
          }
        }
      }
    }

    &:disabled,
    &[disabled] {
      & + #{$this}__text {
        color: var(--gray-50);

        &::before {
          background: var(--theme-color-over-card);
          border-color: var(--theme-color-border);
          cursor: not-allowed;
        }
      }

      &#{$this}__input:checked + #{$this}__text {
        &::before {
          background: var(--theme-color-over-card);
          border: solid 1px var(--theme-color-border);
          cursor: not-allowed;
        }

        &::after {
          background-image: url('./assets/checkIcon-inactive.svg');
          cursor: not-allowed;
        }
      }
    }
  }

  &__error {
    @include caption1;

    color: var(--red-50);
    margin-top: 3px;
  }

  &--error {
    #{$this}__text {
      &::before {
        border-color: var(--red-50);
      }
    }
  }
}
</style>
